import { useSearchParams, Link } from "react-router-dom";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import { Loader2, XCircle } from "lucide-react";
import usePaymentPoll from "@/hooks/usePaymentPoll";
import PaidCelebration from "@/pages/payment/PaidCelebration";

function PollingState({ attempts, maxAttempts }) {
  return (
    <div className="text-center" data-testid="payment-polling">
      <Loader2 size={42} className="animate-spin text-[#cfff04] mx-auto mb-6" />
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-3">
        // Confirming payment
      </div>
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">
        Processing your launch...
      </h1>
      <p className="text-[#e2e2e2]">
        Stripe is confirming your card. This takes a few seconds (attempt {attempts + 1}/{maxAttempts}).
      </p>
    </div>
  );
}

function ExpiredState() {
  return (
    <div className="text-center" data-testid="payment-expired">
      <XCircle size={42} className="text-[#ffd700] mx-auto mb-6" />
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ffd700] mb-3">
        // Session expired
      </div>
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">
        Checkout expired.
      </h1>
      <p className="text-[#e2e2e2] mb-6">No charge was made. Try again from your coin page.</p>
      <Link
        to="/create"
        className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-6 py-3"
      >
        Back to wizard
      </Link>
    </div>
  );
}

function FailedState({ sessionId }) {
  return (
    <div className="text-center" data-testid="payment-failed">
      <XCircle size={42} className="text-[#ff3333] mx-auto mb-6" />
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#ff3333] mb-3">
        // Status unknown
      </div>
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-3">
        We couldn&apos;t confirm your payment.
      </h1>
      <p className="text-[#e2e2e2] mb-6">
        Don&apos;t worry — if Stripe charged you, your coin will be auto-published within a minute (webhook will retry).
        You can also message support with this session: <span className="font-mono text-[#cfff04]">{sessionId}</span>
      </p>
      <Link
        to="/explore"
        className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-6 py-3"
      >
        Browse coins
      </Link>
    </div>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { state, coin, sku, attempts, maxAttempts } = usePaymentPoll(sessionId);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="payment-success-page">
        <div className="max-w-2xl mx-auto px-6 md:px-10 pt-12">
          {state === "polling" && <PollingState attempts={attempts} maxAttempts={maxAttempts} />}
          {state === "paid" && <PaidCelebration coin={coin} sku={sku} />}
          {state === "expired" && <ExpiredState />}
          {state === "failed" && <FailedState sessionId={sessionId} />}
        </div>
      </main>
      <Footer />
    </div>
  );
}
